import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  #refreshCustomVisibility = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    this.refs.stickyBar.inert = true;

    this.#markStickyButtonAsProxy();
    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    this.addEventListener('click', (event) => {
      if (!event.target.closest('.sticky-add-to-cart__choose')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.handleChooseCanvasClick();
    }, { capture: true, signal });
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  #markStickyButtonAsProxy() {
    const { addToCartButton } = this.refs;
    addToCartButton.dataset.cbnStickyProxy = 'true';
    addToCartButton.classList.remove('add-to-cart-button');

    const accessibleName =
      addToCartButton.textContent?.trim() || this.#targetAddToCartButton?.textContent?.trim() || 'Add to cart';
    addToCartButton.setAttribute('aria-label', accessibleName);
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    if (this.dataset.productId === '7910867271936') {
      this.#setupCustomVisibility(productForm, buyButtonsBlock, footer);
      return;
    }

    const mobileViewport = window.matchMedia('(max-width: 749px)');
    const updateVisibility = (entry) => {
      const currentBlock = this.#getProductForm()?.closest('.buy-buttons-block') || buyButtonsBlock;
      const rect = currentBlock.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      const beforeButton = rect.top >= window.innerHeight;
      this.refs.stickyBar.dataset.mode = mobileViewport.matches && beforeButton ? 'choose' : 'add';
      if (isVisible || this.#hiddenByBottom) {
        this.#hideStickyBar();
      } else if (rect.top < 0 || (mobileViewport.matches && beforeButton)) {
        this.#showStickyBar();
      } else {
        this.#hideStickyBar();
      }
    };
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0]) updateVisibility(entries[0]);
    });
    mobileViewport.addEventListener('change', () => {
      this.#buyButtonsIntersectionObserver.unobserve(buyButtonsBlock);
      this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    }, { signal: this.#abortController.signal });

    let frame = 0;
    const scheduleVisibility = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        updateVisibility();
      });
    };
    window.addEventListener('scroll', scheduleVisibility, { passive: true, signal: this.#abortController.signal });
    window.addEventListener('resize', scheduleVisibility, { passive: true, signal: this.#abortController.signal });
    this.#abortController.signal.addEventListener('abort', () => cancelAnimationFrame(frame), { once: true });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }


  #setupCustomVisibility(productForm, initialBlock, footer) {
    const mobileViewport = window.matchMedia('(max-width: 749px)');
    const { signal } = this.#abortController;
    let observedBlock = initialBlock;
    let position = 'visible';
    this.#hiddenByBottom = false;

    const update = () => {
      const mode = mobileViewport.matches && position === 'below' ? 'choose' : 'add';
      const bar = this.refs.stickyBar;
      if (bar.dataset.mode !== mode) bar.dataset.mode = mode;
      const show = !this.#hiddenByBottom &&
        (position === 'above' || (mobileViewport.matches && position === 'below'));
      if (show !== this.#isStuck) {
        if (show) this.#showStickyBar();
        else this.#hideStickyBar();
      }
    };

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const entry = entries.find((item) => item.target === observedBlock);
      if (!entry) return;
      position = entry.isIntersecting ? 'visible' :
        entry.boundingClientRect.bottom <= (entry.rootBounds?.top ?? 0) ? 'above' : 'below';
      update();
    });
    this.#mainBottomObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.#hiddenByBottom = entry.isIntersecting;
      update();
    }, { rootMargin: '200px 0px 0px 0px' });

    this.#refreshCustomVisibility = () => {
      if (signal.aborted) return;
      const form = this.#getProductForm();
      const block = form?.closest('.buy-buttons-block');
      if (!block) return;
      this.#buyButtonsIntersectionObserver.unobserve(observedBlock);
      observedBlock = block;
      this.#buyButtonsIntersectionObserver.observe(observedBlock);
      this.#targetAddToCartButton = form.querySelector('[ref="addToCartButton"]');
    };

    mobileViewport.addEventListener('change', this.#refreshCustomVisibility, { signal });
    window.addEventListener('resize', this.#refreshCustomVisibility, { passive: true, signal });
    window.addEventListener('scrollend', this.#refreshCustomVisibility, { passive: true, signal });
    this.#buyButtonsIntersectionObserver.observe(observedBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  handleChooseCanvasClick = () => {
    const section = this.closest('.shopify-section');
    const picker = section?.querySelector('.variant-picker--guided-steps') || section?.querySelector('variant-picker');
    if (!picker) return;
    picker.scrollIntoView({ block: 'center', behavior: 'instant' });
    const selected = picker.querySelector('input:checked') || picker.querySelector('input,select,button');
    selected?.focus({ preventScroll: true });
  };

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });
    this.#markStickyButtonAsProxy();

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    this.#refreshCustomVisibility?.();

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    const variant = event.detail.resource;
    const variantId = variant?.id;
    if (!variantId) return;

    this.dataset.currentVariantId = variantId;

    if (typeof variant.available === 'boolean') {
      this.refs.addToCartButton.disabled = !variant.available;
      this.dataset.variantAvailable = String(variant.available);
      this.#updateButtonText();
    }
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    stickyBar.inert = false;
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    stickyBar.inert = true;
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    const quantityText = this.#currentQuantity.toString();
    if (quantityNumber.textContent !== quantityText) {
      if (quantityNumber.childNodes.length === 1 && quantityNumber.firstChild.nodeType === Node.TEXT_NODE) {
        quantityNumber.firstChild.nodeValue = quantityText;
      } else {
        quantityNumber.textContent = quantityText;
      }
    }

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
